import { CheckCircle2, Circle } from "lucide-react";
import { Panel, StatusBadge, formatDate } from "@/components/admin/ui";

const Row = ({ label, value, children, testId }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">{label}</dt>
    <dd className="text-right font-semibold text-slate-900 dark:text-white" data-testid={testId}>{children || value}</dd>
  </div>
);

export default function OrgRightPanel({ org, requirements, ownerInvitationStatus, limits, usage, quote, onGoToChecklist }) {
  return (
    <div className="space-y-6" data-testid="org-right-panel">
      <Panel title="Onboarding Progress">
        <div className="mb-3 flex items-baseline justify-between">
          <span className="text-3xl font-extrabold text-slate-950 dark:text-white" data-testid="org-onboarding-percent">{requirements.percent}%</span>
          <span className="text-sm text-slate-500 dark:text-slate-400">{requirements.completed_count}/{requirements.total_count}</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-[#0052FF] to-[#00D4FF] transition-all duration-500" style={{ width: `${requirements.percent}%` }} />
        </div>
      </Panel>

      <Panel
        title="Activation Requirements"
        action={<button type="button" onClick={onGoToChecklist} data-testid="org-right-panel-view-checklist" className="text-xs font-bold text-blue-600 hover:underline dark:text-blue-400">View all</button>}
      >
        <div className="space-y-2" data-testid="org-right-panel-requirements">
          {requirements.items.filter((item) => item.applicable).map((item) => (
            <div key={item.key} className="flex items-center gap-2 text-sm">
              {item.complete ? <CheckCircle2 size={15} className="shrink-0 text-emerald-500" /> : <Circle size={15} className="shrink-0 text-slate-300 dark:text-slate-600" />}
              <span className={item.complete ? "text-slate-700 dark:text-slate-300" : "text-slate-500 dark:text-slate-500"}>{item.label}</span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Organization Summary">
        <dl className="space-y-3 text-sm" data-testid="org-right-panel-summary">
          <Row label="Status" testId="org-summary-status"><StatusBadge status={org.status} testId="org-summary-status-badge" /></Row>
          <Row label="Organization ID" value={org.org_id} testId="org-summary-org-id" />
          <Row label="Created" value={formatDate(org.created_at)} testId="org-summary-created" />
          <Row label="Industry" value={org.industry || "—"} testId="org-summary-industry" />
          <Row label="Source Quote" value={quote?.quote_id || "—"} testId="org-summary-quote" />
          <Row label="Owner Status" value={ownerInvitationStatus} testId="org-summary-owner-status" />
          <Row label="Org Admins" value={`${usage?.org_admins ?? 0} / ${limits?.max_org_admins ?? "—"}`} testId="org-summary-admins" />
          <Row label="Teams" value={`${usage?.teams ?? 0} / ${limits?.max_teams ?? "—"}`} testId="org-summary-teams" />
          <Row label="Nova Billing Users" value={`${usage?.nova_billing_users ?? 0} / ${limits?.max_nova_billing_users ?? "—"}`} testId="org-summary-nova-billing" />
        </dl>
      </Panel>
    </div>
  );
}
